import { useCallback, useEffect, useReducer, useRef } from 'react';
import ackApi from '../../api/ackApi';
import { getDisplayVariant, hasPractice } from './displayVariant';
import { computeSessionEnd } from './session';
import { isSessionExpiry, practiceErrorKind, startErrorMessageId, validationErrorMessageId, PRACTICE_ERRORS } from './errors';
import { clearVisit, loadVisit, saveVisit } from './visitStore';

export const EXAMPLE_COUNT = 3;

export const initialState = {
    status: 'loading', // loading | intro | ready | failed
    startErrorId: null,
    convention: null,
    variant: null,
    // Kept apart from the convention: the expired screen still shows the tenant link rules after the
    // convention content is dropped (HLvgihbj E3).
    ackSettings: null,
    sessionEnd: null,
    expired: false,
    acknowledged: false,
    validation: { pending: false, errorId: null },
    examples: { status: 'idle', items: [] }, // idle | loading | ready | hidden
    exercise: { status: 'idle', item: null, verdict: null, solution: null, typed: null, checking: false, errorKind: null, limited: false },
    score: { correct: 0, total: 0 },
};

export const reducer = (state, action) => {
    // Once the session is over, nothing changes any more (the convention is gone, only the expiry notice remains).
    if (state.expired && action.type !== 'EXPIRE') return state;
    switch (action.type) {
        case 'INTRO':
            // The link is only opened: ack-start waits for the explicit « Commencer » click (HLvgihbj E1).
            return { ...state, status: 'intro' };
        case 'START_PENDING':
            return { ...state, status: 'loading' };
        case 'START_OK':
            return {
                ...state,
                status: 'ready',
                convention: action.convention,
                variant: getDisplayVariant(action.convention && action.convention.conventionParameters),
                ackSettings: (action.convention && action.convention.ackSettings) || null,
                sessionEnd: action.sessionEnd,
                acknowledged: !!action.acknowledged,
            };
        case 'START_FAIL':
            return { ...state, status: 'failed', startErrorId: action.messageId };
        case 'EXAMPLES_LOADING':
            return { ...state, examples: { status: 'loading', items: [] } };
        case 'EXAMPLES_OK':
            return action.items.length
                ? { ...state, examples: { status: 'ready', items: action.items } }
                : { ...state, examples: { status: 'hidden', items: [] } };
        case 'EXAMPLES_FAIL':
            return { ...state, examples: { status: 'hidden', items: [] } };
        case 'EXERCISE_LOADING':
            return { ...state, exercise: { ...state.exercise, status: 'loading', errorKind: null } };
        case 'EXERCISE_OK':
            return action.item
                ? {
                    ...state,
                    exercise: { ...initialState.exercise, limited: state.exercise.limited, status: 'ready', item: action.item },
                }
                : { ...state, exercise: { ...initialState.exercise, status: 'hidden' } };
        case 'EXERCISE_FAIL':
            return {
                ...state,
                exercise: {
                    ...state.exercise,
                    // Nothing was ever shown: without a challenge the section only keeps its message.
                    status: state.exercise.item ? 'ready' : 'error',
                    errorKind: action.kind,
                    limited: state.exercise.limited || action.kind === PRACTICE_ERRORS.TOO_MANY,
                },
            };
        case 'CHECK_PENDING':
            return { ...state, exercise: { ...state.exercise, checking: true, errorKind: null } };
        case 'CHECK_OK':
            return {
                ...state,
                exercise: {
                    ...state.exercise,
                    checking: false,
                    verdict: action.verdict,
                    solution: action.solution || null,
                    typed: action.typed,
                },
                score: {
                    correct: state.score.correct + (action.verdict === 'CORRECT' ? 1 : 0),
                    total: state.score.total + 1,
                },
            };
        case 'CHECK_FAIL':
            return {
                ...state,
                exercise: {
                    ...state.exercise,
                    checking: false,
                    errorKind: action.kind,
                    limited: state.exercise.limited || action.kind === PRACTICE_ERRORS.TOO_MANY,
                },
            };
        case 'VALIDATE_PENDING':
            return { ...state, validation: { pending: true, errorId: null } };
        case 'VALIDATE_OK':
            return { ...state, acknowledged: true, validation: { pending: false, errorId: null } };
        case 'VALIDATE_FAIL':
            return { ...state, validation: { pending: false, errorId: action.messageId } };
        case 'EXPIRE':
            if (state.acknowledged) return state;
            // The convention leaves the state — and so the DOM — entirely (HLvgihbj E3): only the
            // "time is up" notice remains, with the tenant link rules kept in `ackSettings`.
            return {
                ...state,
                expired: true,
                convention: null,
                variant: null,
                validation: { pending: false, errorId: null },
                examples: { status: 'hidden', items: [] },
                exercise: { ...initialState.exercise, status: 'hidden' },
                score: { correct: 0, total: 0 },
            };
        default:
            return state;
    }
};

// State and actions of the convention page. ack-start fires on the explicit « Commencer » click only — never at
// mount, so opening the link consumes no opening (HLvgihbj E1) — once per visit (kept in the tab, visitStore.js).
// Practice is verified by the engine (the verdict shown is always the one of `check`), a session expiry stops
// every call.
export const useConventionPage = ({ ackToken, language, api = ackApi, now = Date.now }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const expiredRef = useRef(false);
    const validatingRef = useRef(false);
    const checkingRef = useRef(false);
    const startingRef = useRef(false);
    const aliveRef = useRef(true);
    const stateRef = useRef(state);
    stateRef.current = state;

    const expire = useCallback(() => {
        if (stateRef.current.acknowledged || expiredRef.current) return;
        expiredRef.current = true;
        clearVisit(ackToken);
        dispatch({ type: 'EXPIRE' });
    }, [ackToken]);

    const loadExercise = useCallback(() => {
        if (expiredRef.current) return;
        dispatch({ type: 'EXERCISE_LOADING' });
        api.getPractice(ackToken, language, 'EXERCISE')
            .then((practice) => {
                const items = practice && practice.practiceAvailable && Array.isArray(practice.items) ? practice.items : [];
                dispatch({ type: 'EXERCISE_OK', item: items[0] || null });
            })
            .catch((error) => {
                if (isSessionExpiry(error)) expire();
                else dispatch({ type: 'EXERCISE_FAIL', kind: practiceErrorKind(error) });
            });
    }, [ackToken, language, api, expire]);

    const loadExamples = useCallback(() => {
        if (expiredRef.current) return;
        dispatch({ type: 'EXAMPLES_LOADING' });
        api.getPractice(ackToken, language, 'EXAMPLE')
            .then((practice) => {
                const items = practice && practice.practiceAvailable && Array.isArray(practice.items) ? practice.items : [];
                dispatch({ type: 'EXAMPLES_OK', items: items.slice(0, EXAMPLE_COUNT) });
            })
            .catch((error) => {
                if (isSessionExpiry(error)) expire();
                else dispatch({ type: 'EXAMPLES_FAIL' });
            });
    }, [ackToken, language, api, expire]);

    const begin = useCallback((visit) => {
        dispatch({ type: 'START_OK', ...visit });
        const variant = getDisplayVariant(visit.convention && visit.convention.conventionParameters);
        // After the validation back-hia refuses practice: nothing to load on a reload.
        if (!visit.acknowledged && hasPractice(variant)) {
            loadExamples();
            loadExercise();
        }
    }, [loadExamples, loadExercise]);

    useEffect(() => () => {
        aliveRef.current = false;
    }, []);

    useEffect(() => {
        const kept = loadVisit(ackToken, now());
        // A visit kept in the tab (session running, or already validated) never goes through the intro again;
        // anything else waits on the Start screen — ack-start is NOT called at mount.
        if (kept) begin(kept);
        else dispatch({ type: 'INTRO' });
        // One gate per mounted page (token / language come from the URL of the mail).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ackToken, language]);

    // The single ack-start of the visit, fired by the « Commencer » click (HLvgihbj E1).
    const start = useCallback(() => {
        if (stateRef.current.status !== 'intro' || startingRef.current) return;
        startingRef.current = true;
        dispatch({ type: 'START_PENDING' });
        api.startAck(ackToken, language)
            .then(({ data, serverDate }) => {
                if (!aliveRef.current) return;
                const visit = { convention: data, sessionEnd: computeSessionEnd(data, serverDate, now()), acknowledged: false };
                saveVisit(ackToken, visit);
                begin(visit);
            })
            .catch((error) => {
                if (aliveRef.current) dispatch({ type: 'START_FAIL', messageId: startErrorMessageId(error) });
            });
    }, [ackToken, language, api, now, begin]);

    const check = useCallback((answer) => {
        const { exercise, acknowledged } = stateRef.current;
        if (expiredRef.current || acknowledged || checkingRef.current || !exercise.item || exercise.limited) return;
        if (typeof answer !== 'string' || answer.length === 0) return;
        checkingRef.current = true;
        dispatch({ type: 'CHECK_PENDING' });
        api.checkPractice(ackToken, language, exercise.item.practiceId, answer)
            .then((result) => {
                checkingRef.current = false;
                dispatch({ type: 'CHECK_OK', verdict: result && result.verdict, solution: result && result.solution, typed: answer });
            })
            .catch((error) => {
                checkingRef.current = false;
                if (isSessionExpiry(error)) expire();
                else dispatch({ type: 'CHECK_FAIL', kind: practiceErrorKind(error) });
            });
    }, [ackToken, language, api, expire]);

    const another = useCallback(() => {
        const { exercise, acknowledged } = stateRef.current;
        if (expiredRef.current || acknowledged || exercise.limited || exercise.status === 'loading') return;
        loadExercise();
    }, [loadExercise]);

    const validate = useCallback(() => {
        const { acknowledged, sessionEnd } = stateRef.current;
        if (expiredRef.current || acknowledged || validatingRef.current) return;
        validatingRef.current = true;
        dispatch({ type: 'VALIDATE_PENDING' });
        api.acknowledge(ackToken)
            .then(() => {
                validatingRef.current = false;
                // Only { acknowledged, sessionEnd } stays in the tab: the convention is dropped right away.
                saveVisit(ackToken, { sessionEnd, acknowledged: true });
                dispatch({ type: 'VALIDATE_OK' });
            })
            .catch((error) => {
                validatingRef.current = false;
                if (isSessionExpiry(error)) expire();
                else dispatch({ type: 'VALIDATE_FAIL', messageId: validationErrorMessageId(error) });
            });
    }, [ackToken, api, expire]);

    return { state, start, check, another, validate, expire };
};

export default useConventionPage;
